<script setup>
/* eslint-env vue/setup-compiler-macros */
import { computed } from 'vue'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import { useI18n } from '@/i18n'

const props = defineProps({
  mappings: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  canEdit: { type: Boolean, default: false }
})

const mappings = computed(() => props.mappings)
const loading = computed(() => props.loading)
const error = computed(() => props.error)
const canEdit = computed(() => props.canEdit)

const emit = defineEmits(['add', 'edit', 'delete'])

const { t } = useI18n()

const mappingKey = (mapping) =>
  `${mapping?.tactic?.id || 't'}-${mapping?.technique?.id || 'none'}`
</script>

<template>
<pFieldset class="legend-transparent dashboard-widget">
    <template #legend>
      <div class="flex items-center pl-2">
        <span class="font-bold p-2">{{ t('siemWorkflowDetails.mitreTitle') }}</span>
      </div>
    </template>
    <div class="flex justify-content-between gap-2 mb-2">
      <Button
        v-if="canEdit"
        icon="pi pi-plus"
        :label="t('siemWorkflowDetails.mappingsAdd')"
        size="small"
        severity="secondary"
        @click="emit('add')"
      />
    </div>
    <div v-if="loading" class="muted">{{ t('siemWorkflowDetails.mitreLoading') }}</div>
    <div v-else-if="error" class="muted error-text">{{ error }}</div>
    <div v-else-if="!mappings.length" class="empty-state">
      <p class="muted">{{ t('siemWorkflowDetails.mitreEmpty') }}</p>
    </div>
    <div v-else class="mapping-grid">
      <div v-for="mapping in mappings" :key="mappingKey(mapping)" class="mapping-card">
        <div class="mapping-row">
          <Tag :value="`${mapping.tactic.mitre_id} ${mapping.tactic.name}`" severity="info" />
          <Tag v-if="mapping.tactic.domain" :value="mapping.tactic.domain" severity="contrast" />
          <Tag
            v-if="mapping.technique"
            :value="`${mapping.technique.mitre_id} ${mapping.technique.name}`"
            severity="secondary"
          />
          <Tag v-if="mapping.technique?.domain" :value="mapping.technique.domain" severity="contrast" />
        </div>
        <div class="mapping-row">
          <Tag :value="`${t('siemWorkflowDetails.mitreConfidence')}: ${mapping.confidence}`" severity="success" />
          <span v-if="mapping.rationale" class="rationale" :title="mapping.rationale">{{ mapping.rationale }}</span>
          <div class="mapping-actions" v-if="canEdit">
            <Button icon="pi pi-pencil" text size="small" @click="emit('edit', mapping)" />
            <Button icon="pi pi-trash" text size="small" severity="danger" @click="emit('delete', mapping)" />
          </div>
        </div>
      </div>
    </div>
  </pFieldset>
</template>

<style scoped>
.muted { color: var(--p-text-color-secondary); }

.error-text { color: var(--p-red-500, #ef4444); }

.empty-state {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px dashed var(--p-content-border-color, rgba(0,0,0,.1));
  border-radius: 8px;
}

.mapping-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
}

.mapping-card {
  border: 1px solid var(--p-content-border-color, rgba(0,0,0,.08));
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--p-surface-50, rgba(0,0,0,.012));
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mapping-row {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
}

.mapping-actions {
  margin-left: auto;
  display: flex;
  gap: 4px;
}

.rationale {
  font-size: .82rem;
  color: var(--p-text-color-secondary);
  font-style: italic;
}

:global(.my-app-dark) .mapping-card {
  border-color: var(--p-surface-700, #334155);
  background: var(--p-surface-800, #1e293b);
}
</style>
